<template>
    <div class="prefilter-config">
        <div class="prefilter-header">
            <div class="prefilter-toggle">
                <a-switch v-model="localFilters.enabled" @change="onEnabledChange" />
                <span class="toggle-label">{{ $t('automation.enablePreFilters') }}</span>
            </div>
            <div class="prefilter-description" v-if="!localFilters.enabled">
                {{ $t('automation.preFilterDescription') }}
            </div>
        </div>

        <div v-if="localFilters.enabled" class="prefilter-body">
            <!-- Filter Groups -->
            <div v-for="(group, groupIndex) of localFilters.groups" :key="group.id || `group-${groupIndex}`" class="filter-group-container">
                <div class="filter-group">
                    <!-- Group header with remove button -->
                    <div v-if="localFilters.groups.length > 1" class="group-header">
                        <span class="group-title">Grup {{ groupIndex + 1 }}</span>
                        <a-button
                            type="text"
                            icon="delete"
                            size="small"
                            danger
                            @click="removeGroup(groupIndex)"
                        >
                            Grubu Sil
                        </a-button>
                    </div>

                    <!-- Filters in group -->
                    <div v-for="(filter, filterIndex) of group.filters" :key="filter.id || `filter-${filterIndex}`" class="filter-row">
                        <span class="filter-text">
                            <!-- Prefix with inline operator selector for non-first filters -->
                            <span v-if="filterIndex === 0" class="prefix">Eğer</span>
                            <InlineSelectOperator
                                v-else
                                v-model="group.operator"
                                :options="[
                                    { label: 've', value: 'and' },
                                    { label: 'veya', value: 'or' }
                                ]"
                                class="prefix-operator"
                            />
                            
                            <!-- Attribute with natural language -->
                            <InlineSelect
                                v-model="filter.attribute"
                                :options="getAvailableAttributes()"
                                :placeholder="'alan seçin'"
                                :searchable="true"
                                @change="onAttributeChange(filter)"
                            />
                            
                            <!-- Operator -->
                            <InlineSelectOperator
                                v-if="filter.attribute"
                                v-model="filter.rule"
                                :options="getOperatorsForFilter(filter)"
                                :placeholder="'operatör'"
                            />
                            
                            <!-- Value (only if operator requires it) -->
                            <template v-if="filter.attribute && !['empty', 'non_empty', 'not_empty'].includes(filter.rule)">
                                <!-- For options/select types -->
                                <InlineSelectValue
                                    v-if="hasOptions(filter.attribute) && !isMultiSelect(filter)"
                                    v-model="filter.value"
                                    :options="getOptions(filter.attribute)"
                                    :placeholder="'değer seçin'"
                                    :searchable="true"
                                />
                                <!-- For multi-select (array contains) -->
                                <span v-else-if="isMultiSelect(filter)" class="multi-value-wrapper">
                                    <a-select
                                        v-model="filter.value"
                                        mode="multiple"
                                        class="multi-select-inline"
                                        :placeholder="'seçin'"
                                    >
                                        <a-select-option v-for="opt in getOptions(filter.attribute)" :key="opt.value" :value="opt.value">
                                            {{ opt.label }}
                                        </a-select-option>
                                    </a-select>
                                </span>
                                <!-- Boolean -->
                                <InlineSelectValue
                                    v-else-if="getAttributeType(filter.attribute) === 'boolean'"
                                    v-model="filter.value"
                                    :options="[
                                        { label: 'Evet', value: true },
                                        { label: 'Hayır', value: false }
                                    ]"
                                    :placeholder="'seçin'"
                                />
                                <!-- Regular text/number input -->
                                <InlineInput
                                    v-else
                                    v-model="filter.value"
                                    :type="getAttributeType(filter.attribute) === 'number' ? 'number' : 'text'"
                                    :placeholder="'değer girin'"
                                />
                            </template>
                        </span>

                        <!-- Remove button -->
                        <a-button
                            type="text"
                            size="small"
                            class="remove-btn"
                            @click="removeFilter(groupIndex, filterIndex)"
                        >
                            <i class="material-icons">delete_outline</i>
                        </a-button>
                    </div>

                    <!-- Add filter to group button -->
                    <div class="add-condition-btn" @click="addFilterToGroup(groupIndex)">
                        <i class="material-icons">add</i>
                        <span>Koşul ekle</span>
                    </div>
                </div>

                <!-- Group operator separator -->
                <div
                    v-if="localFilters.groups.length > 1 && groupIndex !== localFilters.groups.length - 1"
                    class="group-operator-row"
                >
                    <div class="separator-line"></div>
                    <div class="operator-badge">
                        <a-select
                            v-model="localFilters.operator"
                            size="small"
                            class="operator-selector"
                        >
                            <a-select-option value="and">VE (AND)</a-select-option>
                            <a-select-option value="or">VEYA (OR)</a-select-option>
                        </a-select>
                    </div>
                    <div class="separator-line"></div>
                </div>
            </div>

            <!-- Add group button -->
            <div class="add-group-btn" @click="addFilterGroup">
                <i class="material-icons">add_circle_outline</i>
                <span>Filtre Grubu Ekle</span>
            </div>
        </div>
    </div>
</template>

<script>
import { generateRandomId } from '../../../../utils/session';
import _ from 'lodash';
import { mapState } from 'vuex';
import InlineSelect from './InlineSelect.vue';
import InlineSelectOperator from './InlineSelectOperator.vue';
import InlineSelectValue from './InlineSelectValue.vue';
import InlineInput from './InlineInput.vue';

export default {
    name: 'PreFilterConfig',
    components: {
        InlineSelect,
        InlineSelectOperator,
        InlineSelectValue,
        InlineInput,
    },
    props: {
        value: {
            type: Object,
            default: () => ({
                enabled: false,
                operator: 'and',
                groups: [],
            }),
        },
        automation: {
            type: Object,
            required: false,
        },
    },
    data() {
        return {
            localFilters: _.cloneDeep(this.value),
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels || [],
            users: (state) => state.user.users.data || [],
            allTags: (state) => state.tag.tags.data || [],
            subjects: (state) => state.subject.subjects.data || [],
        }),
        departments() {
            // Filter out deleted and get current language name
            return this.subjects
                .filter(s => !s.isDeleted && s.isActive)
                .map(s => ({
                    label: _.get(s, `languageContent.${this.$i18n.locale}.name`, s.name),
                    value: s._id
                }));
        },
        contactTags() {
            // Filter contact type tags only
            return this.allTags
                .filter(t => t.type === 'contact')
                .map(t => ({
                    label: t.name,
                    value: t._id
                }));
        },
    },
    methods: {
        onEnabledChange(enabled) {
            if (enabled && this.localFilters.groups.length === 0) {
                this.addFilterGroup();
            }
        },
        addFilterGroup() {
            const newGroup = {
                id: generateRandomId(),
                operator: 'and',
                filters: [],
            };
            this.localFilters.groups.push(newGroup);
            // Add first filter to new group
            this.addFilterToGroup(this.localFilters.groups.length - 1);
        },
        addFilterToGroup(groupIndex) {
            const newFilter = {
                id: generateRandomId(),
                attribute: '',
                rule: 'equal',
                value: '',
            };
            this.localFilters.groups[groupIndex].filters.push(newFilter);
        },
        removeFilter(groupIndex, filterIndex) {
            this.localFilters.groups[groupIndex].filters.splice(filterIndex, 1);
            // If group becomes empty, remove it
            if (this.localFilters.groups[groupIndex].filters.length === 0 && this.localFilters.groups.length > 1) {
                this.localFilters.groups.splice(groupIndex, 1);
            }
        },
        removeGroup(groupIndex) {
            this.localFilters.groups.splice(groupIndex, 1);
        },
        onAttributeChange(filter) {
            // Set default operator and value based on attribute type
            const attr = this.getAvailableAttributes().find(a => a.value === filter.attribute);
            if (attr) {
                filter.rule = attr.defaultOperator || 'equal';
                filter.value = attr.defaultValue || '';
            }
        },
        getAvailableAttributes() {
            const eventType = _.get(this.automation, 'options.event.type', '');
            
            // Message event attributes - with natural Turkish labels (message → conversation → visitor)
            if (eventType.startsWith('message.')) {
                return [
                    { label: "Mesajın Geldiği Chat'in Kanalı", value: 'conversation.channel', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'in Platformu", value: 'conversation.ref', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'in Dil Kodu", value: 'conversation.languageCode', valueType: 'string', defaultOperator: 'equal' },
                    { label: "Mesajın Geldiği Chat'in Departmanı", value: 'conversation.subject', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'in Durumu", value: 'conversation.status', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'i Başlatan", value: 'conversation.startedFrom', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'in Ana Site Adresi", value: 'conversation.homeSite', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Mesajın Geldiği Chat'in Puanı", value: 'conversation.point', valueType: 'number', defaultOperator: 'equal' },
                    { label: "Mesajın Geldiği Chat'i Kapatan Kullanıcı", value: 'conversation.destroyedUser', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'i Chatbot Kapattı mı", value: 'conversation.destroyedByChatBot', valueType: 'boolean', defaultOperator: 'equal' },
                    { label: "Mesajın Geldiği Chat'i Ziyaretçi Kapattı mı", value: 'conversation.destroyedByVisitor', valueType: 'boolean', defaultOperator: 'equal' },
                    { label: "Mesajın Geldiği Chat'e İlk Bağlanan Kullanıcı", value: 'conversation.connectedUser', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'in Cevap Durumu", value: 'conversation.answerStatus', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'teki Ad Soyad", value: 'conversation.fullname', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Mesajın Geldiği Chat'teki E-posta", value: 'conversation.email', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Mesajın Geldiği Chat'teki Müşterinin Kanalı", value: 'visitor.channel', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'teki Müşterinin Platformu", value: 'visitor.platform', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'teki Müşterinin Adı", value: 'visitor.fullname', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Mesajın Geldiği Chat'teki Müşterinin E-postası", value: 'visitor.email', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Mesajın Geldiği Chat'teki Müşterinin Telefon Numarası", value: 'visitor.phone', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Mesajın Geldiği Chat'teki Müşterinin WhatsApp Numarası", value: 'visitor.whatsappNumber', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Mesajın Geldiği Chat'teki Müşterinin Sahibi", value: 'visitor.owner', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'teki Müşterinin Etiketleri", value: 'visitor.tags', valueType: 'array', defaultOperator: 'contain', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'teki Müşteri VIP mi", value: 'visitor.vipCustomer', valueType: 'boolean', defaultOperator: 'equal' },
                    { label: "Mesajın Geldiği Chat'teki Müşterinin Cevap Durumu", value: 'visitor.answerStatus', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Mesajın Geldiği Chat'teki Müşterinin Tercih Ettiği Dil", value: 'visitor.preferredLang', valueType: 'string', defaultOperator: 'equal' },
                    { label: "Mesajın Geldiği Chat'teki Müşteri Meta Reklamdan Geldi mi", value: 'visitor.metaReferrals', valueType: 'array', defaultOperator: 'non_empty' },
                    { label: "Mesajın Geldiği Chat'teki Müşterinin Ziyaret Ettiği Sayfa", value: 'visitor.visitingPage', valueType: 'string', defaultOperator: 'contain' },
                ];
            }
            
            // Chat event attributes - with natural Turkish labels
            if (eventType.startsWith('chat.')) {
                return [
                    { label: "Chat'in Geldiği Kanal", value: 'conversation.channel', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Chat'in Platformu", value: 'conversation.ref', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Chat'in Dil Kodu", value: 'conversation.languageCode', valueType: 'string', defaultOperator: 'equal' },
                    { label: "Chat'in Departmanı", value: 'conversation.subject', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Chat'in Durumu", value: 'conversation.status', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Chat'i Başlatan", value: 'conversation.startedFrom', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Chat'in Ana Site Adresi", value: 'conversation.homeSite', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Chat'in Puanı", value: 'conversation.point', valueType: 'number', defaultOperator: 'equal' },
                    { label: "Chat'i Kapatan Kullanıcı", value: 'conversation.destroyedUser', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Chat'i Chatbot Kapattı mı", value: 'conversation.destroyedByChatBot', valueType: 'boolean', defaultOperator: 'equal' },
                    { label: "Chat'i Ziyaretçi Kapattı mı", value: 'conversation.destroyedByVisitor', valueType: 'boolean', defaultOperator: 'equal' },
                    { label: "Chat'e İlk Bağlanan Kullanıcı", value: 'conversation.connectedUser', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Chat'in Cevap Durumu", value: 'conversation.answerStatus', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Chat'teki Ad Soyad", value: 'conversation.fullname', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Chat'teki E-posta", value: 'conversation.email', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Kişinin Kanalı", value: 'visitor.channel', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Kişinin Platformu", value: 'visitor.platform', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Kişinin Adı", value: 'visitor.fullname', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Kişinin E-postası", value: 'visitor.email', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Kişinin Telefon Numarası", value: 'visitor.phone', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Kişinin Sahibi", value: 'visitor.owner', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Kişinin Etiketleri", value: 'visitor.tags', valueType: 'array', defaultOperator: 'contain', hasOptions: true },
                    { label: "Kişi VIP mi", value: 'visitor.vipCustomer', valueType: 'boolean', defaultOperator: 'equal' },
                ];
            }
            
            // Contact event attributes - with natural Turkish labels
            if (eventType.startsWith('contact.')) {
                const baseAttributes = [
                    { label: "Kişinin Kanalı", value: 'visitor.channel', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Kişinin Platformu", value: 'visitor.platform', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Kişinin Adı", value: 'visitor.fullname', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Kişinin E-postası", value: 'visitor.email', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Kişinin Telefon Numarası", value: 'visitor.phone', valueType: 'string', defaultOperator: 'contain' },
                    { label: "Kişinin Sahibi", value: 'visitor.owner', valueType: 'string', defaultOperator: 'equal', hasOptions: true },
                    { label: "Kişi VIP mi", value: 'visitor.vipCustomer', valueType: 'boolean', defaultOperator: 'equal' },
                    { label: "Kişinin Cevap Durumu", value: 'visitor.answerStatus', valueType: 'enum', defaultOperator: 'equal', hasOptions: true },
                    { label: "Kişinin Etiketleri", value: 'visitor.tags', valueType: 'array', defaultOperator: 'contain', hasOptions: true },
                    { label: "Kişinin Tercih Ettiği Dil", value: 'visitor.preferredLang', valueType: 'string', defaultOperator: 'equal' },
                    { label: "Kişi Meta Reklamdan Geldi mi", value: 'visitor.metaReferrals', valueType: 'array', defaultOperator: 'non_empty' },
                    { label: "Kişinin Ziyaret Ettiği Sayfa", value: 'visitor.visitingPage', valueType: 'string', defaultOperator: 'contain' },
                ];
                
                // contact.updated için "Güncellenen Alan" attribute'u ekle
                if (eventType === 'contact.updated') {
                    return [
                        { label: "Güncellenen Alan", value: '_updatedFieldName', valueType: 'enum', defaultOperator: 'in_list', hasOptions: true, isSpecial: true },
                        ...baseAttributes,
                    ];
                }
                
                return baseAttributes;
            }
            
            return [];
        },
        getAttributeType(attributeValue) {
            const attr = this.getAvailableAttributes().find(a => a.value === attributeValue);
            return attr ? attr.valueType : 'string';
        },
        hasOptions(attributeValue) {
            const attr = this.getAvailableAttributes().find(a => a.value === attributeValue);
            return attr && attr.hasOptions;
        },
        isMultiSelect(filter) {
            // Array type için multi-select
            if (this.getAttributeType(filter.attribute) === 'array') {
                return ['in_list', 'not_in_list', 'contain', 'not_contain'].includes(filter.rule);
            }
            // Owner field için "şunlardan biri" operatöründe multi-select
            if (filter.attribute === 'visitor.owner' && ['in_list', 'not_in_list'].includes(filter.rule)) {
                return true;
            }
            // Güncellenen Alan için multi-select
            if (filter.attribute === '_updatedFieldName' && ['in_list', 'not_in_list'].includes(filter.rule)) {
                return true;
            }
            return false;
        },
        getOptions(attributeValue) {
            // Güncellenen Alan options (contact.updated için)
            if (attributeValue === '_updatedFieldName') {
                return [
                    { label: 'Ad Soyad', value: 'fullname' },
                    { label: 'E-posta', value: 'email' },
                    { label: 'Telefon', value: 'phone' },
                    { label: 'WhatsApp Numarası', value: 'whatsappNumber' },
                    { label: 'Tercih Edilen Dil', value: 'preferredLang' },
                    { label: 'Sahip', value: 'owner' },
                    { label: 'VIP Müşteri', value: 'vipCustomer' },
                    { label: 'Etiketler', value: 'tags' },
                    { label: 'İlgili Departmanlar', value: 'relatedDepartments' },
                    { label: 'Tercih Edilen Departman', value: 'preferredDepartment' },
                    { label: 'Tercih Edilen Temsilciler', value: 'preferredAgents' },
                    { label: 'Cevap Durumu', value: 'answerStatus' },
                    { label: 'Engellenme Durumu', value: 'isBlocked' },
                    { label: 'Ziyaret Edilen Sayfa', value: 'visitingPage' },
                    { label: 'Referer', value: 'referer' },
                    { label: 'IP Adresi', value: 'ipAddress' },
                    { label: 'Zoho Bilgisi', value: 'zohoInfo' },
                    { label: 'Meta Lead Bilgisi', value: 'metaLeadInfo' },
                    { label: 'Meta Referrals', value: 'metaReferrals' },
                ];
            }
            
            // Platform options
            if (attributeValue === 'conversation.ref' || attributeValue === 'visitor.platform') {
                return [
                    { label: 'WhatsApp', value: 'whatsapp' },
                    { label: 'Instagram', value: 'instagram' },
                    { label: 'Telegram', value: 'telegram' },
                    { label: 'Web', value: 'web' },
                    { label: 'Messenger', value: 'messenger' },
                    { label: 'Trendyol', value: 'trendyol' },
                    { label: 'LinkedIn', value: 'linkedin' },
                ];
            }
            
            // Channel options
            if (attributeValue === 'conversation.channel' || attributeValue === 'visitor.channel') {
                return this.channels.filter(ch => !ch.isDeleted).map(ch => ({
                    label: ch.name,
                    value: ch._id
                }));
            }
            
            // Department options
            if (attributeValue === 'conversation.subject') {
                return this.departments;
            }
            
            // Status options
            if (attributeValue === 'conversation.status') {
                return [
                    { label: 'Chatbot', value: 'chatbot' },
                    { label: 'New', value: 'new' },
                    { label: 'Active', value: 'active' },
                    { label: 'Lost', value: 'lost' },
                    { label: 'Destroyed', value: 'destroyed' },
                    { label: 'Private', value: 'private' },
                ];
            }
            
            // Started From options
            if (attributeValue === 'conversation.startedFrom') {
                return [
                    { label: 'User', value: 'user' },
                    { label: 'Visitor', value: 'visitor' },
                    { label: 'API', value: 'api' },
                    { label: 'Broadcast', value: 'broadcast' },
                    { label: 'Instagram Comment', value: 'instagram-comment' },
                    { label: 'Automation', value: 'automation' },
                ];
            }
            
            // User options
            if (attributeValue === 'conversation.destroyedUser' || 
                attributeValue === 'conversation.connectedUser' || 
                attributeValue === 'visitor.owner') {
                return this.users.map(user => ({
                    label: user.name,
                    value: user._id
                }));
            }
            
            // Answer Status options
            if (attributeValue === 'conversation.answerStatus' || attributeValue === 'visitor.answerStatus') {
                return [
                    { label: 'Answered', value: 'answered' },
                    { label: 'Unanswered', value: 'unanswered' },
                ];
            }
            
            // Tags options
            if (attributeValue === 'visitor.tags') {
                return this.contactTags;
            }
            
            return [];
        },
        getOperatorsForFilter(filter) {
            const type = this.getAttributeType(filter.attribute);
            const operators = [];
            
            // Güncellenen Alan için özel operatörler
            if (filter.attribute === '_updatedFieldName') {
                operators.push(
                    { value: 'in_list', label: 'şunlardan biri güncellendi' },
                    { value: 'not_in_list', label: 'şunlardan hiçbiri güncellenmedi' },
                    { value: 'equal', label: 'bu alan güncellendi' },
                    { value: 'not_equal', label: 'bu alan güncellenmedi' }
                );
                return operators;
            }
            
            // Owner field için özel operatörler
            if (filter.attribute === 'visitor.owner' || 
                filter.attribute === 'conversation.destroyedUser' || 
                filter.attribute === 'conversation.connectedUser') {
                operators.push(
                    { value: 'equal', label: 'eşittir' },
                    { value: 'not_equal', label: 'eşit değildir' },
                    { value: 'in_list', label: 'şunlardan biri' },
                    { value: 'not_in_list', label: 'şunlardan hiçbiri değil' },
                    { value: 'empty', label: 'sahipsiz' },
                    { value: 'non_empty', label: 'sahipli' }
                );
                return operators;
            }
            
            switch (type) {
                case 'string':
                    operators.push(
                        { value: 'equal', label: 'eşittir' },
                        { value: 'not_equal', label: 'eşit değildir' },
                        { value: 'contain', label: 'içerir' },
                        { value: 'not_contain', label: 'içermez' },
                        { value: 'starts_with', label: 'ile başlar' },
                        { value: 'ends_with', label: 'ile biter' },
                        { value: 'empty', label: 'boş' },
                        { value: 'non_empty', label: 'dolu' },
                        { value: 'regex', label: 'regex ile eşleşir' },
                        { value: 'in_list', label: 'bunlardan biri' },
                        { value: 'not_in_list', label: 'bunlardan hiçbiri değil' }
                    );
                    break;
                case 'number':
                    operators.push(
                        { value: 'equal', label: 'eşittir' },
                        { value: 'not_equal', label: 'eşit değildir' },
                        { value: 'greater_than', label: 'büyüktür' },
                        { value: 'less_than', label: 'küçüktür' },
                        { value: 'greater_or_equal', label: 'büyük veya eşittir' },
                        { value: 'less_or_equal', label: 'küçük veya eşittir' },
                        { value: 'empty', label: 'boş' },
                        { value: 'non_empty', label: 'dolu' }
                    );
                    break;
                case 'boolean':
                    operators.push(
                        { value: 'equal', label: 'eşittir' },
                        { value: 'not_equal', label: 'eşit değildir' }
                    );
                    break;
                case 'enum':
                    operators.push(
                        { value: 'equal', label: 'eşittir' },
                        { value: 'not_equal', label: 'eşit değildir' },
                        { value: 'empty', label: 'boş' },
                        { value: 'non_empty', label: 'dolu' }
                    );
                    break;
                case 'array':
                    operators.push(
                        { value: 'contain', label: 'içerir' },
                        { value: 'not_contain', label: 'içermez' },
                        { value: 'empty', label: 'boş' },
                        { value: 'non_empty', label: 'dolu' }
                    );
                    break;
                default:
                    operators.push(
                        { value: 'equal', label: 'eşittir' },
                        { value: 'not_equal', label: 'eşit değildir' },
                        { value: 'empty', label: 'boş' },
                        { value: 'non_empty', label: 'dolu' }
                    );
            }
            
            return operators;
        },
    },
    watch: {
        localFilters: {
            handler(newValue) {
                this.$emit('input', newValue);
            },
            deep: true,
        },
        value: {
            handler(newValue) {
                if (!_.isEqual(newValue, this.localFilters)) {
                    this.localFilters = _.cloneDeep(newValue);
                }
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
.prefilter-config {
    padding: 20px;
    background: #fafafa;
    border-radius: 8px;

    .prefilter-header {
        margin-bottom: 20px;

        .prefilter-toggle {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 10px;

            .toggle-label {
                font-weight: 600;
                font-size: 16px;
            }
        }

        .prefilter-description {
            color: #666;
            font-size: 14px;
            line-height: 1.5;
        }
    }

    .prefilter-body {
        .filter-group-container {
            margin-bottom: 20px;

            .filter-group {
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                padding: 20px;
                box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

                .group-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 15px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #f0f0f0;

                    .group-title {
                        font-weight: 600;
                        color: #666;
                        font-size: 13px;
                        text-transform: uppercase;
                    }
                }

                .filter-row {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 12px;
                    font-size: 15px;
                    line-height: 32px;

                    .filter-text {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        flex: 1;
                        flex-wrap: wrap;

                        .prefix {
                            color: #666;
                            font-weight: 500;
                            margin-right: 4px;
                        }

                        .prefix-operator {
                            margin-right: 4px;
                        }

                        .multi-value-wrapper {
                            display: inline-flex;
                            align-items: center;
                        }
                    }

                    .remove-btn {
                        opacity: 0;
                        transition: all 0.2s ease;
                        color: #ff4d4f;
                        padding: 4px;
                        min-width: auto;

                        .material-icons {
                            font-size: 18px;
                        }

                        &:hover {
                            background: #fff1f0;
                            transform: scale(1.1);
                        }
                    }

                    &:hover .remove-btn {
                        opacity: 1;
                    }
                }

                .add-condition-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    color: #1890ff;
                    cursor: pointer;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                    font-size: 14px;
                    margin-top: 8px;

                    &:hover {
                        background: #e6f7ff;
                    }

                    i {
                        font-size: 18px;
                    }
                }
            }
        }

        .group-operator-row {
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 20px 0;
            gap: 15px;

            .separator-line {
                flex: 1;
                height: 2px;
                background: linear-gradient(to right, transparent, #d9d9d9, transparent);
            }

            .operator-badge {
                background: #f9f0ff;
                padding: 8px 18px;
                border-radius: 20px;
                border: 2px solid #d3adf7;
                box-shadow: 0 2px 8px rgba(146, 84, 222, 0.15);
                transition: all 0.2s ease;

                &:hover {
                    background: #efdbff;
                    border-color: #b37feb;
                    transform: scale(1.02);
                }

                .operator-selector {
                    min-width: 120px;
                    font-weight: 600;
                    font-size: 14px;

                    :deep(.ant-select-selection) {
                        border: none !important;
                        background: transparent !important;
                        box-shadow: none !important;
                        color: #9254de;
                        font-weight: 700;
                    }

                    :deep(.ant-select-arrow) {
                        color: #9254de;
                    }
                }
            }
        }

        .add-group-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 12px 20px;
            background: white;
            border: 2px dashed #d9d9d9;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-top: 20px;
            font-weight: 500;

            &:hover {
                border-color: #1890ff;
                color: #1890ff;
                background: #f0f8ff;
            }

            i {
                font-size: 22px;
            }
        }
    }
}
</style>

<style lang="scss">
.prefilter-config {
    .multi-select-inline {
        min-width: 200px !important;
        
        .ant-select-selection {
            border: none !important;
            border-bottom: 2px solid #52c41a !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            background: transparent !important;

            .ant-select-selection__rendered {
                margin: 0 !important;
                padding: 2px 8px !important;
                line-height: 28px !important;
            }

            .ant-select-selection__choice {
                background: #f6ffed !important;
                border: 1px solid #95de64 !important;
                color: #52c41a !important;
                font-weight: 500;
            }

            &:hover {
                background: #f6ffed !important;
            }
        }

        &.ant-select-focused .ant-select-selection {
            border-bottom-color: #389e0d !important;
            box-shadow: 0 2px 4px rgba(82, 196, 26, 0.2) !important;
        }
    }
}
</style>

